// apps/cli/src/lib/bootstrap-status.ts
import { createHash, randomUUID } from "node:crypto";
import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import {
  explainRejection, parseStatusDocForReader, STATUS_BLOB, STATUS_CONTAINER, type StatusDoc,
} from "@m8t-stack/install-contract";
import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";

// The status document's shape lives in @m8t-stack/install-contract, not here.
// It used to be an interface in this file parsed with an unchecked cast, which
// is how `stampSeed`, `updaterProvision`, `platformVersion` and `warnings` all
// shipped undeclared: nothing ever compared what the installer wrote against
// what this file claimed it wrote. The contract package declares the fields
// once, validates them at runtime, and is checked against the REAL writer by a
// cross-language test — so the same drift now fails CI instead of shipping.
export type { StatusDoc } from "@m8t-stack/install-contract";
export { STATUS_CONTAINER, STATUS_BLOB };

/** Deterministic SA name — MUST match installer/entrypoint.sh's `m8tinst$(sha256(RG+SUB)|cut -c1-12)`. */
export function deriveStatusSaName(resourceGroup: string, subscriptionId: string): string {
  const hex = createHash("sha256").update(resourceGroup + subscriptionId).digest("hex").slice(0, 12);
  return `m8tinst${hex}`;
}

export function statusBlobUrl(saName: string): string {
  return `https://${saName}.blob.core.windows.net/${STATUS_CONTAINER}/${STATUS_BLOB}`;
}

/** Read + parse the status blob using the Owner's az creds (SA key, mgmt plane). */
export async function readStatusBlob(opts: {
  saName: string; resourceGroup: string; subscriptionId: string;
}): Promise<StatusDoc> {
  let key: string;
  let raw: string;
  try {
    key = (await runAz([
      "storage", "account", "keys", "list",
      "-n", opts.saName, "-g", opts.resourceGroup, "--subscription", opts.subscriptionId,
      "--query", "[0].value", "-o", "tsv",
    ])).trim();
    if (!key) throw new Error("empty storage-account key");
  } catch (e) {
    throw new LocalCliError({
      code: "BOOTSTRAP_STATUS_UNREADABLE",
      message: `Could not read the installer status storage account '${opts.saName}'.`,
      hint: "The installer may not have reached its first phase yet — wait a few seconds and retry.",
      cause: e,
    });
  }
  // Download to a real temp file, not `/dev/stdout`: `az storage blob download`
  // requires a SEEKABLE target stream and errors ("Target stream handle must be
  // seekable") when handed a pipe. Read the file back, then clean it up.
  const tmpFile = path.join(os.tmpdir(), `m8t-status-${randomUUID()}.json`);
  try {
    await runAz([
      "storage", "blob", "download",
      "--account-name", opts.saName, "--account-key", key,
      "--container-name", STATUS_CONTAINER, "--name", STATUS_BLOB,
      "--file", tmpFile, "--no-progress", "--only-show-errors",
    ]);
    raw = await fs.readFile(tmpFile, "utf8");
  } catch (e) {
    throw new LocalCliError({
      code: "BOOTSTRAP_STATUS_UNREADABLE",
      message: "The installer status blob is not readable yet.",
      hint: "Phase 1 (status storage) may not have completed. Retry shortly, or check 'az container logs'.",
      cause: e,
    });
  } finally {
    await fs.rm(tmpFile, { force: true });
  }

  // Validate rather than cast — but as a READER, which tolerates fields it does
  // not know. A founder's CLI must not refuse a document because a newer
  // installer added one; strictness lives in CI, where the contract's drift test
  // fails a developer instead of an install.
  //
  // The code below deliberately does NOT reuse BOOTSTRAP_STATUS_UNREADABLE:
  // `bootstrap status --watch` treats that code as "the installer has not
  // written the blob yet" and keeps polling, so raising it for a document that
  // IS present but malformed would turn a hard, knowable failure into an
  // infinite wait. A blob that exists and does not match the contract is a
  // different fact and gets its own code, which propagates.
  const parsed = parseStatusDocForReader(raw);
  if (!parsed.ok) {
    throw new LocalCliError({
      code: "BOOTSTRAP_STATUS_INVALID",
      message: `The installer status document could not be read: ${explainRejection(parsed.rejection)}`,
      hint: hintFor(parsed.rejection.reason),
    });
  }
  return parsed.doc;
}

/** Remedy copy per rejection reason — the causes have opposite fixes. */
function hintFor(reason: "not-json" | "missing-schema-version" | "unknown-schema-version" | "invalid"): string {
  switch (reason) {
    case "unknown-schema-version":
      return "The installer wrote a newer status format than this CLI understands. Update the CLI: npm i -g @m8t-stack/cli";
    case "missing-schema-version":
    case "not-json":
      return "The status blob looks truncated or corrupt. Check the installer directly: az container logs -n <aci> -g <rg>.";
    case "invalid":
      // Reached only for a type/shape violation, since unknown fields are
      // tolerated above — so "your CLI is older" is a real possibility here too.
      return "Your CLI may be older than the installer that wrote this. Update it (npm i -g @m8t-stack/cli); if that does not help, check: az container logs -n <aci> -g <rg>.";
  }
}
